import { Link } from 'react-router-dom';
import { MessageCircle, Send, Mail } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';

export default function Footer() {
  const { site } = useSite();
  const [email, setEmail] = useState('');

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post('/newsletter', { email });
      toast.success('Subscribed — you will hear about new batches and research releases.');
      setEmail('');
    } catch { toast.error('Enter a valid email address'); }
  };

  return (
    <footer className="bg-[#0B0F12] text-[#FAFAF8] mt-24" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <p className="font-display font-black text-3xl">VQ HEALTH</p>
            <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95] mt-1">Research Labs · United Kingdom</p>
            <p className="text-sm text-[#9AA0A6] mt-4 max-w-md leading-relaxed">{site.footer_text}</p>
            <div className="flex items-center gap-3 mt-6">
              {site.whatsapp_url && (
                <a data-testid="footer-whatsapp" href={site.whatsapp_url} target="_blank" rel="noreferrer"
                  className="w-10 h-10 border border-[#2A3138] flex items-center justify-center hover:bg-[#1F8A58] hover:border-[#1F8A58] transition-colors" aria-label="WhatsApp">
                  <MessageCircle className="w-4 h-4" />
                </a>
              )}
              {site.telegram_url && (
                <a data-testid="footer-telegram" href={site.telegram_url} target="_blank" rel="noreferrer"
                  className="w-10 h-10 border border-[#2A3138] flex items-center justify-center hover:bg-[#6366F1] hover:border-[#6366F1] transition-colors" aria-label="Telegram">
                  <Send className="w-4 h-4" />
                </a>
              )}
              {site.email_general && (
                <a data-testid="footer-email" href={`mailto:${site.email_general}`}
                  className="w-10 h-10 border border-[#2A3138] flex items-center justify-center hover:bg-[#FAFAF8] hover:text-[#0B0F12] transition-colors" aria-label="Email">
                  <Mail className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
          <div>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#8A8F95] mb-4">Catalogue</p>
            <div className="space-y-2.5 text-sm">
              <Link to="/products" className="block text-[#C9CDD2] hover:text-white transition-colors">All Products</Link>
              <Link to="/products?format=Vial" className="block text-[#C9CDD2] hover:text-white transition-colors">Vials</Link>
              <Link to="/products?format=Pen" className="block text-[#C9CDD2] hover:text-white transition-colors">Pens</Link>
              <Link to="/products?format=Spray" className="block text-[#C9CDD2] hover:text-white transition-colors">Sprays</Link>
              <Link to="/compare" className="block text-[#C9CDD2] hover:text-white transition-colors">Compare</Link>
              <Link to="/coas" className="block text-[#C9CDD2] hover:text-white transition-colors">COA Library</Link>
            </div>
          </div>
          <div>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#8A8F95] mb-4">Company</p>
            <div className="space-y-2.5 text-sm">
              <Link to="/journal" className="block text-[#C9CDD2] hover:text-white transition-colors">Research Journal</Link>
              <Link to="/docs" className="block text-[#C9CDD2] hover:text-white transition-colors">Documentation Centre</Link>
              <Link to="/faq" className="block text-[#C9CDD2] hover:text-white transition-colors">FAQ</Link>
              <Link to="/contact" className="block text-[#C9CDD2] hover:text-white transition-colors">Contact</Link>
              <Link to="/docs/terms" className="block text-[#C9CDD2] hover:text-white transition-colors">Terms</Link>
              <Link to="/docs/privacy" className="block text-[#C9CDD2] hover:text-white transition-colors">Privacy</Link>
              <Link to="/docs/research-disclaimer" className="block text-[#C9CDD2] hover:text-white transition-colors">Research Disclaimer</Link>
            </div>
          </div>
        </div>
        <form onSubmit={subscribe} className="mt-12 pt-8 border-t border-[#1E242B] flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
          <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#8A8F95]">Batch release alerts & research notes</p>
          <div className="flex gap-2">
            <input data-testid="footer-newsletter-input" value={email} onChange={(e) => setEmail(e.target.value)}
              type="email" required placeholder="your@email.com"
              className="bg-transparent border border-[#2A3138] px-4 py-2.5 text-sm w-64 focus:outline-none focus:border-[#FAFAF8] transition-colors" />
            <button data-testid="footer-newsletter-button" className="bg-[#FAFAF8] text-[#0B0F12] px-5 py-2.5 text-xs font-mono2 uppercase tracking-widest hover:bg-white transition-colors">
              Subscribe
            </button>
          </div>
        </form>
        <div className="mt-8 pt-6 border-t border-[#1E242B] flex flex-col sm:flex-row gap-3 justify-between">
          <p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-[#6B7178]">
            For in-vitro laboratory research only. Not for human or veterinary use.
          </p>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-[#6B7178]">© {new Date().getFullYear()} VQ Health · vqhealth.co.uk</p>
        </div>
      </div>
    </footer>
  );
}
